<template>
  <div :class="['book-content-box', isDark ? 'theme-dark' : '']">
    <!-- header -->
    <van-nav-bar :border="false" fixed>
      <template #left>
        <van-icon @click="goBack" name="arrow-left back-icon" />
        {{ currentChapter.name }}
      </template>
    </van-nav-bar>
    <!-- book content -->
    <template v-if="!loading">
      <div class="content-box" :style="contentStyle" ref="content" @click="toggleMenu" v-html="content"></div>
      <!-- lock modal -->
      <div v-if="showLockModal" @click="toggleMenu" class="unlock-alert-box">
        <div class="unlock-modal"></div>
        <div class="flex-center unlock-tip">
          <div class="title fw500">{{ currentChapter.name }}</div>
          <div class="tip">
            Dear reader, this chapter need to be paid. The following content will be more attractive.
          </div>
          <van-button
            size="large"
            :loading="unlocking"
            loading-text="Unlocking"
            @click.stop="handleUnlock"
            round
            class="unlock-btn"
          >
            <div>Unlock This Chapter</div>
            <div class="flex-center coin">
              <img src="@/assets/images/gcoin.png" />
              {{ chapterCostCoins }}
            </div>
          </van-button>
          <div class="coin-balance">Coin balance:{{ myCoins }} Coins</div>
        </div>
      </div>
      <!-- prev & next buttons -->
      <div class="footer-actions">
        <van-button v-if="!isFirst" round size="small" @click="goChapter('PREV')" class="btn prev-btn">
          Previous Chapter
        </van-button>
        <van-button v-if="!isLast" round size="small" @click="goChapter('NEXT')" class="btn next-btn">
          Next Chapter
        </van-button>
      </div>
    </template>
    <!-- loading icon -->
    <div class="loading-box flex-center" v-else>
      <van-loading size="24px" vertical>Loading...</van-loading>
    </div>
    <!-- bottom menu -->
    <transition name="van-slide-up">
      <NvBottomMenu v-if="showBottomMenu" @click-menu="showSideList" />
    </transition>
    <!-- left chapter list -->
    <van-popup overlay-class="chapter-box-overlay" v-model="showChapterList" position="left" class="chapter-box">
      <NvChapterList
        @chpt-click="handleChapterClick"
        :current="currentChapter"
        :chapters="chapterList"
        :book="bookInfo"
      />
    </van-popup>
    <!-- recharge box -->
    <van-popup v-model="showStoreBox" position="bottom" class="store-popup-box">
      <div class="store-card">
        <NvRechargeCard ref="store" :redirect="currentPageLink">
          <div slot="prepend" class="store-card-header">
            <div class="flex-center store-title">
              <span class="fw500">Products</span>
              <span @click="viewAll">view all ></span>
            </div>
            <div class="store-tip">
              You don't have enough coins, please top up!
            </div>
          </div>
        </NvRechargeCard>
      </div>
    </van-popup>
    <!-- add to shelf button -->
    <transition name="van-fade">
      <div class="like-btn" v-if="bookStatus < 2 && !showLockModal">
        <van-icon @click="addToShelf" v-if="bookStatus === 0" name="like-o" />
        <van-icon v-if="bookStatus === 1" name="like" />
      </div>
    </transition>
    <!-- confirmation box -->
    <NvConfirm
      :show="showConfirmBox"
      @click.native="showConfirmBox = false"
      @close="showConfirmBox = false"
      @cancel="handleCancel"
      @confirm="handleConfirm"
      cancelText="Later"
      content="You can check your story in the library anytime."
      title="Add To Library?"
    />
    <NvConfirm
      title="Purchase Failed"
      content="An error occurred during the purchasing process. If you haven't received your coins, please tap on Restore to continue with your order. "
      confirmText="Restore"
      @confirm="handleRestore"
      @close="showRestoreModal = false"
      :showCancel="false"
      :show="showRestoreModal"
    />
  </div>
</template>

<script>
import {
  getBookChapters,
  getBookContent,
  getBookInfo,
  saveBooksToLib,
  getBooksFromLib,
  getGlobalConfig,
  updateReadProgress,
  getBookReadProgress,
  unlockChapter,
  getUserInfo, // refresh coin balance
  buyCoins
} from "@/service/api";
import { transformBookContent, setToStorage, getFromStorage } from "@/service/utils";
import NvBottomMenu from "@/components/widget/book/NvBottomMenu";
import NvChapterList from "@/components/widget/book/NvChapterList";
import NvConfirm from "@/components/widget/NvConfirm";
import NvRechargeCard from "@/components/widget/my/NvRechargeCard";

// because `this.$toast` is not working on some IOS devices
import { Toast } from "vant";

const pIndent = "&nbsp;&nbsp;&nbsp;&nbsp;";
const FROM_KEY = "CONTENT_FROM";
const WORDS_COUNT = 1000;
const COIN_KEY = "LAST_PAID_COIN_TYPE";
const PAY_METHOD_KEY = "LAST_PAY_METHOD";

// the apis are very messed so the logic here is too much

export default {
  components: {
    NvBottomMenu,
    NvChapterList,
    NvConfirm,
    NvRechargeCard
  },
  data() {
    return {
      content: "",
      chapterList: [],
      currentChapter: {},
      bookId: null,
      bookName: "", // used as parameter of recordEvent func
      bookInfo: {},
      loading: true,
      showBottomMenu: false,
      showChapterList: false,
      showConfirmBox: false,
      showStoreBox: false,
      shelfBooks: [],
      bookStatus: 3, // 0 => not in shelf, 1 => added to shelf now, 2 => in shelf, 3 => other hidden cases
      showRestoreModal: false,
      leavePermission: false, // this is a backdoor, takes higher priority
      pending: false, // to avoid clicking pay button multiple times
      unlocking: false
    };
  },
  computed: {
    isDark() {
      return this.$store.state.readMode === "DARK";
    },
    isFirst() {
      return this.getReadChapterIndex() === 0;
    },
    isLast() {
      const list = this.chapterList;
      return this.getReadChapterIndex() === list.length - 1;
    },
    isPolyFont() {
      return this.$store.state.font === "POLY";
    },
    contentStyle() {
      const { font, background, fontSize } = this.$store.state;
      const base = { fontSize: `${fontSize}px` };
      if (font === "POLY") {
        base.fontFamily = "poly";
      }
      if (background) {
        base.background = background.value;
      }
      if (this.showLockModal) {
        base.overflow = "hidden";
        base.height = "30vh";
        base.minHeight = "100%";
      }
      return base;
    },
    myCoins() {
      return this.$store.state.user.coins_amount || 0;
    },
    showLockModal() {
      return this.currentChapter.unlock === false;
    },
    chapterCostCoins() {
      let words = this.currentChapter.word_count;
      words = words < 1000 ? 1000 : words;
      return Math.round(words / WORDS_COUNT);
    },
    currentPageLink() {
      // to avoid duplicated error in Vue Router
      const path = this.$route.fullPath;
      const qs = new URLSearchParams(path);
      qs.delete("orderId");
      qs.delete("status");
      const append = decodeURIComponent(qs.toString());
      return window.location.origin + "/#" + append;
    }
  },
  watch: {
    isLast(val) {
      val && window.$event("BookView", { bookName: this.bookName });
    },
    showBottomMenu(val) {
      val && window.$event("ReadMenu_Show", {});
    },
    showLockModal(val) {
      val &&
        window.$event("Locked_Page_Show", {
          bookName: this.bookName,
          chapterName: this.currentChapter.name
        });
    }
  },
  methods: {
    viewAll() {
      this.leavePermission = true;
      this.$router.push({ name: "recharge-store" });
    },
    addToShelf() {
      saveBooksToLib(this.bookId).then(() => {
        this.bookStatus = 1;
        Toast({
          message: "added to bookshelf successfully",
          className: "toast-box"
        });
        // do not rely on api because it could be delayed
        // so here we make a cheat
        // this.getMyShelf();
        this.shelfBooks.push({ id: this.bookId });
        setTimeout(() => {
          this.bookStatus = 2;
        }, 100);
      });
    },
    getReadChapterIndex() {
      const idList = this.chapterList.map(i => i.id);
      const currentId = this.currentChapter.id;
      return idList.indexOf(currentId);
    },
    handleUnlock() {
      if (this.unlocking === true) return;
      window.$event("Unlocked_button_Click", {
        bookName: this.bookName,
        chapterName: this.currentChapter.name
      });
      this.unlocking = true;
      this.navigateWithAuth(
        () => {
          unlockChapter({
            book_id: this.bookId,
            chapter_id: this.currentChapter.id
          })
            .then(res => {
              if (res.code === 102) {
                this.showStoreBox = true;
                window.$event("Charge_Dialog_Show", {});
              } else {
                window.$event("Successfully_Unlocked", {});
                this.handlePostUnlock();
                this.currentChapter.unlock = true;
              }
            })
            .finally(() => {
              this.unlocking = false;
            });
        },
        () => {
          this.leavePermission = true;
        }
      );
    },
    handlePostUnlock() {
      this.getChapterList();
      getUserInfo().then(res => this.$store.commit("SET_USER", res));
    },
    handleConfirm() {
      window.$event("Add_to_bookshelf_dialog_click", { from: "OK" });
      this.bookId && saveBooksToLib(this.bookId);
      // we don't do this in callback because we don't want to
      // make user wait if api gets slow
      this.goNext && this.goNext();
    },
    handleCancel() {
      window.$event("Add_to_bookshelf_dialog_click", { from: "Later" });
      this.showConfirmBox = false;
      this.goNext && this.goNext();
    },
    goBack() {
      // this.$router.back();
      const fromRouterName = getFromStorage(FROM_KEY);
      if (fromRouterName) {
        this.$router.replace({ name: fromRouterName });
      } else {
        this.$router.replace({ name: "lib" });
      }
    },
    handleChapterClick(chapter) {
      this.showChapterList = false;
      this.currentChapter = chapter;
      this._getContent(this.bookId, chapter.id);
    },
    scrollEvent() {
      this.showBottomMenu = false;
    },
    showSideList() {
      this.showBottomMenu = false;
      this.showChapterList = true;
    },
    toggleMenu(val) {
      if (typeof val === "boolean") {
        this.showBottomMenu = val;
      } else {
        this.showBottomMenu = !this.showBottomMenu;
      }
    },
    getChapter(tag) {
      const current = this.getReadChapterIndex();
      if (tag === "PREV") {
        window.$event("Click_Previous_Chapter", {
          bookName: this.bookName
        });
        return this.chapterList[current - 1];
      } else if (tag === "NEXT") {
        window.$event("Click_Next_Chapter", { bookName: this.bookName });
        return this.chapterList[current + 1];
      }
    },
    goChapter(tag) {
      const chapter = this.getChapter(tag);
      this.currentChapter = chapter || this.currentChapter;
      this._getContent(this.bookId, this.currentChapter.id);
    },
    getChapterList() {
      const config = this.$store.state.globalConfig;
      const isEmptyConfig = Object.keys(config).length === 0;
      if (isEmptyConfig) {
        return Promise.all([
          getBookChapters(this.bookId), //
          getGlobalConfig() //
        ]).then(([res, globalConfig]) => {
          this.$store.commit("SET_GLOBAL_CONFIG", globalConfig);
          return this.getStatedChapters(res, globalConfig);
        });
      } else {
        return getBookChapters(this.bookId).then(res => {
          return this.getStatedChapters(res, config);
        });
      }
    },
    // add unlock field to chapter list
    getStatedChapters(res, config = {}) {
      const defaultFreeChapterCount = config.default_free_probation_chapters;
      const chapters = res.chapters || res || [];
      const finalList = chapters.map((c, idx) => {
        if (c.unlock !== undefined) {
          return c;
        } else {
          c.unlock = idx < defaultFreeChapterCount;
          return c;
        }
      });
      this.chapterList = finalList;
      return finalList;
    },
    recordChapterIndexForFB() {
      const index = this.getReadChapterIndex();
      const eventMap = {
        4: "ChapterView_5",
        9: "ChapterView_10",
        14: "ChapterView_15",
        19: "ChapterView_20"
      };
      if (eventMap[index]) {
        window.$event(eventMap[index], {});
      }
    },
    _getContent(bookId, chptId) {
      const self = this;
      this.loading = true;
      getBookContent(bookId, chptId)
        .then(res => {
          transformBookContent(res).then(content => {
            self.content = content
              .split(/\n/g)
              .map(i => `${pIndent}${String(i).trim()}`)
              .join("<div class='gap'></div>");
          });
        })
        .finally(() => {
          this.loading = false;
          window.$event("ChapterView", { bookName: this.bookName });
          // record specific chapter index for FB pixel
          this.recordChapterIndexForFB();
          updateReadProgress(
            { book_id: this.bookId, chapter_id: chptId },
            {
              bookName: this.bookName,
              chapters: this.chapterList
            }
          );
        });
    },
    getContent(bookId, chapter) {
      if (chapter) {
        this.currentChapter = chapter;
        this._getContent(bookId, chapter.id);
      } else {
        this.getChapterList().then(chapters => {
          this.currentChapter = chapters[0];
          const chId = chapters[0].id;
          this._getContent(bookId, chId);
        });
      }
    },
    getBookInfo() {
      getBookInfo(this.bookId).then(res => {
        this.bookInfo = res;
      });
    },
    /**
     * get books from my shelf, used when leaving the page
     */
    getMyShelf() {
      getBooksFromLib().then(res => {
        const result = res || [];
        this.shelfBooks = result;
        const isInLib = result.some(b => b.id === this.bookId);
        this.bookStatus = isInLib ? 2 : 0;
      });
    },
    /**
     * get read history, used to locate current read progress
     */
    getHistory() {
      const bookId = this.bookId;
      getBookReadProgress(bookId).then(res => {
        if (res.chapter_id) {
          this.getChapterList().then(chapters => {
            const curChapter = chapters.filter(c => c.id === res.chapter_id);
            this.getContent(bookId, curChapter[0]);
          });
        } else {
          this.getContent(bookId);
        }
      });
    },
    // this.$refs.store.restore();
    // ref fails to work because the content is lazy loaded via v-if
    handleRestore() {
      if (this.pending) return;
      window.$event("Restore_Button_Click", {});
      // read from history, no need to set again
      const lastPaid = getFromStorage(COIN_KEY);
      const lastPayMethod = getFromStorage(PAY_METHOD_KEY); // check?
      if (lastPaid && lastPaid.type) {
        this.pending = true;
        this.navigateWithAuth(() => {
          const code = lastPaid.value;
          const redirectUrl = this.currentPageLink;
          buyCoins({
            commodity: code,
            redirect_url: redirectUrl,
            payment_type: lastPayMethod
          })
            .then(res => {
              this.pending = false;
              window.location.href = res.redirect_url;
            })
            .catch(() => {
              this.pending = false;
            })
            .finally(() => {
              this.showRestoreModal = false;
            });
        });
      }
    },
    handleOrderStatus(order) {
      if (order.status == 2) {
        // using browser back function won't make this order fail
        // so this is meaningless, block for now
        // this.showRestoreModal = true;
        // window.$event("Purchase_Failed_Show", {});
      } else if (order.status == 1) {
        window.$event("Buy_Successfully", {});
        Toast({
          message: "Buy successfully",
          className: "toast-box"
        });
      } else {
        // remove order result query strings
        const newQuery = Object.assign({}, order);
        delete newQuery.orderId;
        delete newQuery.status;
        this.$router.replace({ query: newQuery });
      }
    }
  },
  mounted() {
    const { id: bookId, name, from, status } = this.$route.query;
    status && this.handleOrderStatus(this.$route.query);
    window.$event("EnterRead", { from, bookName: name });
    this.bookId = bookId;
    this.bookName = name;
    this.getBookInfo();
    this.getHistory();
    this.getMyShelf();
    document.addEventListener("scroll", this.scrollEvent);
  },
  destroyed() {
    document.removeEventListener("scroll", this.scrollEvent);
  },
  beforeRouteEnter(to, from, next) {
    from.name && setToStorage(FROM_KEY, from.name);
    next();
  },
  beforeRouteLeave(to, from, next) {
    const libBookIds = this.shelfBooks.map(b => b.id);
    this.goNext = next;
    if (this.leavePermission) {
      next();
    } else if (libBookIds.indexOf(this.bookId) <= -1) {
      window.$event("Add_to_bookshelf_dialog_click", {});
      this.showConfirmBox = true;
    } else {
      next();
    }
  }
};
</script>

<style lang="scss" scoped>
.book-content-box {
  background-color: $bg-main;
  position: relative;
  /* dark style */
  &.theme-dark {
    background-color: $bg-main-d;
    color: $fc-black-d;
    &::v-deep {
      .van-nav-bar {
        background-color: $bg-menu-d;
      }
    }
    .unlock-alert-box {
      background: linear-gradient(180deg, rgba(248, 248, 249, 0.05) 0%, $bg-main-d 35%);
      .unlock-tip {
        background: $bg-main-d;
      }
    }
    .store-popup-box {
      background: $bg-main-d;
      &::v-deep {
        .van-cell {
          background-color: $bg-main-d;
          color: $fc-black-d;
        }
      }
    }
  }
  .content-box {
    min-height: calc(100vh - 45px);
  }
  .like-btn {
    position: fixed;
    right: 16px;
    bottom: 78px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    z-index: 3;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: white;
    font-size: 20px;
    background: linear-gradient(133deg, $bg-l-start 14%, $bg-l-end 85%);
  }
  .unlock-alert-box {
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 7;
    height: calc(100vh - 45px);
    text-align: center;
    background: linear-gradient(180deg, rgba(248, 248, 249, 0.05) 0%, #ffffff 35%);
    .unlock-modal {
      height: 35%;
    }
    .unlock-tip {
      flex-direction: column;
      background: white;
      padding: 20px 36px;
      .title {
        font-size: $fs-xxl;
        color: $color-main;
      }
      .tip {
        font-size: $fs-s;
        color: $fc-grey;
        margin-top: 20px;
        margin-bottom: 20px;
      }
      .unlock-btn {
        background: linear-gradient(135deg, $bg-l-start, $bg-l-end);
        color: white;
        border: none;
        width: 100%;
        font-size: $fs-s;
        font-weight: 500;
        .coin {
          padding-top: 4px;
          img {
            margin-right: 6px;
            width: 16px;
          }
        }
      }
      .coin-balance {
        color: $color-main;
        font-size: $fs-s;
        margin-top: 8px;
      }
    }
  }
  .back-icon {
    font-size: 16px;
    color: $fc-black;
    margin-right: 5px;
  }
  .chapter-box {
    height: 100%;
    width: 80%;
  }
  .store-popup-box {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    height: 90%;
    .store-card {
      padding: 8px 16px;
      .store-card-header {
        width: 100%;
        margin-bottom: 16px;
        .store-title {
          height: 45px;
          justify-content: space-between;
          span:last-child {
            color: $color-main;
          }
        }
        .store-tip {
          color: $fc-grey;
          font-size: $fs-xs;
          font-weight: 300;
        }
      }
    }
  }
  .content-box {
    padding: 16px;
    line-height: 30px;
    margin-top: 45px;
  }
  .loading-box {
    height: 100vh;
  }
  .footer-actions {
    display: flex;
    width: 100%;
    color: white;
    justify-content: center;
    padding: 40px 0;
    font-weight: 500;

    .btn {
      color: white;
      border: none;
      flex: 1;
      margin: 0 30px;
    }
    .prev-btn {
      background: linear-gradient(135deg, $bg-l-end, $bg-l-start);
    }
    .next-btn {
      background: linear-gradient(135deg, $bg-l-start, $bg-l-end);
    }
  }
}
</style>
